import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  PAKISTAN_CITIES,
  computeCityPrices,
  currencyFor,
  formatMoney,
  karatLabel,
  KARATS,
  GRAMS_PER_TOLA,
  type Karat,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { SITE_URL } from "@/lib/seo";

/**
 * /pakistan/urdu — Urdu-language (Nastaliq script) mirror of the Pakistan
 * hub. Uses same live price data; separate URL keeps Urdu search-intent
 * traffic ranked under a dedicated document with hreflang="ur-PK".
 */
export const Route = createFileRoute("/pakistan/urdu")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: () => {
    const url = `${SITE_URL}/pakistan/urdu`;
    return {
      meta: [
        { title: "پاکستان میں آج سونے کا ریٹ — Gold Rate Pakistan (Urdu)" },
        {
          name: "description",
          content:
            "پاکستان کے تمام بڑے شہروں میں آج 24K، 22K اور 21K سونے کا لائیو ریٹ۔ ہر 10 منٹ میں انٹرنیشنل سپاٹ اور USD/PKR سے اپ ڈیٹ۔",
        },
        { property: "og:title", content: "پاکستان میں آج سونے کا ریٹ" },
        {
          property: "og:description",
          content: "کراچی، لاہور، اسلام آباد، پشاور اور دیگر شہروں میں لائیو گولڈ ریٹ۔",
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hreflang: "ur-PK", href: url },
        { rel: "alternate", hreflang: "en-PK", href: `${SITE_URL}/pakistan` },
        { rel: "alternate", hreflang: "x-default", href: `${SITE_URL}/pakistan` },
      ],
    };
  },
  component: PakistanUrduPage,
});

function PakistanUrduPage() {
  const { data } = useGoldRates();
  const currency = currencyFor("pakistan");
  const rows = PAKISTAN_CITIES.map((c) => ({
    city: c,
    prices: computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.pkrPerUsd,
      country: "pakistan",
      cityPremium: c.premium,
    }),
  }));
  const karachi = rows[0];

  return (
    <Layout>
      <section
        dir="rtl"
        lang="ur"
        className="mx-auto max-w-5xl px-4 py-8 font-[system-ui]"
        style={{ fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', system-ui, serif" }}
      >
        <nav className="text-xs text-muted-foreground mb-3" dir="ltr">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › اردو
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold leading-relaxed">
          پاکستان میں آج سونے کا ریٹ
        </h1>
        <p className="mt-3 text-lg leading-loose text-muted-foreground">
          پاکستان کے 21 بڑے شہروں میں 24 قیراط، 22 قیراط اور 21 قیراط سونے کی
          تازہ ترین قیمت۔ یہ ریٹ ہر 10 منٹ میں انٹرنیشنل سپاٹ (XAU/USD) اور
          USD/PKR ایکسچینج ریٹ کی بنیاد پر خودکار طور پر اپ ڈیٹ ہوتا ہے۔
        </p>

        <div className="mt-4" dir="ltr">
          <LiveBadge label="Live" />
        </div>

        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 leading-loose">
          <p>
            آج کراچی سرافہ بازار میں 24 قیراط سونے کی قیمت{" "}
            <strong dir="ltr">{formatMoney(karachi.prices.perTola24, currency)}</strong>{" "}
            فی تولہ ہے۔ 22 قیراط{" "}
            <strong dir="ltr">{formatMoney(karachi.prices.perTola22, currency)}</strong> اور
            21 قیراط{" "}
            <strong dir="ltr">{formatMoney(karachi.prices.perTola21, currency)}</strong> فی تولہ۔
          </p>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" dir="ltr">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="px-3 py-3 font-semibold">City / شہر</th>
                <th className="px-3 py-3 font-semibold text-right">24K / tola</th>
                <th className="px-3 py-3 font-semibold text-right">22K / tola</th>
                <th className="px-3 py-3 font-semibold text-right">21K / tola</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ city, prices }) => (
                <tr key={city.slug} className="border-t border-border">
                  <th scope="row" className="px-3 py-3 text-left font-medium">
                    <Link
                      to="/pakistan/urdu/$city"
                      params={{ city: city.slug }}
                      className="hover:text-primary hover:underline"
                    >
                      {city.name}
                    </Link>
                  </th>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold">
                    {formatMoney(prices.perTola24, currency)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {formatMoney(prices.perTola22, currency)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {formatMoney(prices.perTola21, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <article className="mt-8 leading-loose text-foreground/90">
          <h2 className="text-xl font-bold mb-3">سونے کا ریٹ کیسے متعین ہوتا ہے؟</h2>
          <p>
            پاکستان میں سونے کا ریٹ دو بنیادی چیزوں پر منحصر ہے: پہلا،
            انٹرنیشنل سپاٹ پرائس (XAU/USD) جو لندن، نیویارک اور شنگھائی
            بلین مارکیٹوں میں ہر سیکنڈ تبدیل ہوتی رہتی ہے۔ دوسرا، ڈالر
            کے مقابلے پاکستانی روپے کا ایکسچینج ریٹ (USD/PKR) جو
            انٹربینک مارکیٹ میں طے ہوتا ہے۔
          </p>
          <p className="mt-3">
            کراچی سرافہ بازار پاکستان کا سب سے بڑا حوالہ جاتی مارکیٹ ہے۔
            لاہور، اسلام آباد، راولپنڈی اور پشاور کے دکاندار عام طور پر
            کراچی کے اوپننگ ریٹ کے 0.08% سے 0.15% اوپر ریٹ کوٹ کرتے ہیں
            جس میں ٹرانسپورٹ اور ڈیلر مارجن شامل ہے۔
          </p>
          <p className="mt-3">
            میکنگ چارجز 8% سے 20% تک ہوتے ہیں جو ڈیزائن کی پیچیدگی پر
            منحصر ہے۔ زیورات پر 17% GST بھی لاگو ہوتی ہے۔ خریداری سے
            پہلے تحریری رسید ضرور لیں۔
          </p>
        </article>

        <FaqSection
          faqs={[
            {
              q: "آج کراچی میں سونے کا ریٹ کیا ہے؟",
              a: `آج کراچی میں 24 قیراط سونے کا ریٹ ${formatMoney(karachi.prices.perTola24, currency)} فی تولہ اور ${formatMoney(karachi.prices.perGram24, currency)} فی گرام ہے۔ یہ ریٹ ہر 10 منٹ میں اپ ڈیٹ ہوتا ہے۔`,
            },
            {
              q: "کیا لاہور اور کراچی کا ریٹ ایک جیسا ہوتا ہے؟",
              a: "قومی حوالہ جاتی ریٹ ایک ہی ہوتا ہے لیکن ہر شہر کا مقامی پریمیم مختلف ہے۔ کراچی 0%، لاہور +0.08%، اسلام آباد +0.12% اور پشاور +0.15% تک۔",
            },
            {
              q: "کیا سونے پر زکوٰۃ لاگو ہے؟",
              a: "جی ہاں، اگر آپ کے پاس نصاب (تقریباً 87.48 گرام سونا) سے زیادہ سونا ایک سال سے موجود ہے تو 2.5% زکوٰۃ لازم ہے۔ زکوٰۃ کیلکولیٹر ہماری ٹولز پیج پر موجود ہے۔",
            },
            {
              q: "22 قیراط اور 24 قیراط میں کیا فرق ہے؟",
              a: "24 قیراط 99.9% خالص سونا ہے، 22 قیراط 91.67% خالص۔ زیورات کے لیے 22 قیراط زیادہ مضبوط ہوتا ہے کیونکہ اس میں دوسری دھاتیں مکس ہوتی ہیں۔ 24 قیراط سکوں اور سرمایہ کاری کے لیے بہتر ہے۔",
            },
          ]}
          title="اکثر پوچھے جانے والے سوالات"
        />

        <div className="mt-8 rounded-xl border border-border bg-muted/40 p-4 text-sm" dir="ltr">
          <h3 className="font-semibold">Karats & units</h3>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {(KARATS as readonly Karat[]).filter((k) => k !== "14k").map((k) => (
              <a key={k} href={`/pakistan/${k}`} className="rounded-full border border-border px-3 py-1 hover:border-primary hover:text-primary">
                {karatLabel(k)}
              </a>
            ))}
            {["per-tola", "per-gram", "per-10-gram", "per-ratti"].map((u) => (
              <a key={u} href={`/pakistan/${u}`} className="rounded-full border border-border px-3 py-1 hover:border-primary hover:text-primary">
                {u}
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            1 tola = {GRAMS_PER_TOLA} grams · English version: <Link to="/pakistan" className="text-primary hover:underline">/pakistan</Link>
          </p>
        </div>
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "اردو", href: "https://drhint.com/pakistan/urdu" },
          ]}
        />
        <PakistanNavigator exclude="/pakistan/urdu" />
      </section>
    </Layout>
  );
}